'use client';

import { useEffect, useEffectEvent, type RefObject } from 'react';

/**
 * Runs `onEnter` once, the first time the element nears the viewport, then
 * stops watching. Shared by anything that loads or reveals on scroll so the
 * observe-once logic lives in one place.
 *
 * - `immediate`: if the element is already on screen when the effect runs,
 *   call `onEnter` synchronously instead of arming an observer. Reveal uses
 *   this so content on screen at hydration never blinks out and back.
 * - `onArm`: called just before the observer starts watching, i.e. only when
 *   the element is off screen and `onEnter` will come later.
 * - Without IntersectionObserver, `onEnter` runs on the next tick, so the
 *   content still appears and no state is set synchronously inside the effect.
 */
export function useInViewOnce(
  ref: RefObject<Element | null>,
  onEnter: () => void,
  {
    rootMargin = '0px',
    immediate = false,
    onArm,
  }: { rootMargin?: string; immediate?: boolean; onArm?: () => void } = {},
) {
  const enter = useEffectEvent(onEnter);
  const arm = useEffectEvent(() => onArm?.());

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      const id = setTimeout(enter, 0);
      return () => clearTimeout(id);
    }

    if (immediate) {
      const rect = node.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        enter();
        return;
      }
    }

    arm();
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          enter();
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, rootMargin, immediate]);
}
